import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import React, { useState, useEffect } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { PlusCircle, Copy, Trash2, Pencil, Eye, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useABTestStore } from '@/store/ab-test/abTestStore';
import { ABTestCreateModal } from '@/components/ab-test/ABTestCreateModal';
import { VideoCardUnified } from '@/components/shared/VideoCardUnified';
import { VideoGrid } from '@/components/shared/VideoGrid';
import { EmptyABTestState } from '@/components/ab-test/EmptyABTestState';
import { toast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ABTest, ABTestStatus } from '@/components/ab-test/types';
import { t } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { generateVideoEmbedCodes } from '@/utils/generateVideoEmbedCode';

const formatDate = (dateString: string | undefined | null) => {
  if (!dateString) return t('dateNotAvailable');

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return t('invalidDate');
    }
    return new Intl.DateTimeFormat(getInterfaceLanguage(), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  } catch (error) {
    return t('invalidDate');
  }
};

const ABTestList = () => {
  useLanguage();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTest, setSelectedTest] = useState<string | null>(null);
  const [editingTestId, setEditingTestId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { tests, isLoading, fetchUserTests, deleteTest, updateTest } = useABTestStore();

  // Buscar testes ao carregar a página
  useEffect(() => {
    if (user?.id_user) {
      fetchUserTests(user.id_user);
    }
  }, [user?.id_user, fetchUserTests]);
  
  const handleCreateTest = () => {
    navigate('/ab-test/create');
  };

  const handleToggleStatus = async (id: string) => {
    // TODO: Implementar toggle de status quando o backend suportar
    toast({
      title: t('featureInDevelopment'),
      description: t('toggleStatusDev'),
    });
  };

  const handleOpenRename = (testId: string) => {
    setEditingTestId(testId);
  };

  const handleTitleChange = async (testAbId: string, newTitle: string) => {
    if (!newTitle.trim()) return;

    try {
      // Enviar apenas o nome para renomear o teste
      await updateTest(testAbId, {
        name: newTitle.trim()
      });

      setEditingTestId(null);

      toast({
        title: t('testRenamedTitle'),
        description: t('testRenamedDesc'),
      });

      // Recarregar lista para garantir sincronização
      if (user?.id_user) {
        await fetchUserTests(user.id_user);
      }
    } catch (error) {
      console.error('Erro ao renomear teste:', error);
      toast({
        title: t('renameErrorTitle'),
        description: t('renameTestError'),
        variant: "destructive",
      });
    }
  };

  const handleDuplicate = (id: string) => {
    // TODO: Implementar duplicação quando necessário
    toast({
      title: t('featureInDevelopment'),
      description: t('duplicateDev'),
    });
  };

  const handleCopyCode = async (test: ABTest) => {
    // Verificar se há vídeos no teste
    if (!test.videos || test.videos.length === 0) {
      toast({
        title: t('copyCodeErrorTitle'),
        description: t('noVideosInTest'),
        variant: "destructive",
      });
      return;
    }

    try {
      const firstVideo = test.videos[0];
      const userId = user?.id || user?.id_user || '';

      // Busca dimensões reais do vídeo e gera iframe com padding correto
      const { iframeCode } = await generateVideoEmbedCodes({
        videoId: firstVideo.id,
        userId,
        videoOrigin: 'TESTEAB',
      });

      await navigator.clipboard.writeText(iframeCode);
      toast({
        title: t('codesCopied'),
        description: t('abTestCodeCopiedDesc'),
      });
    } catch (error) {
      console.error('Erro ao gerar código do teste A/B:', error);
      toast({
        title: t('copyCodeErrorTitle'),
        description: t('noVideosInTest'),
        variant: "destructive",
      });
    }
  };

  const handleDelete = (id: string) => {
    setSelectedTest(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (selectedTest) {
      try {
        await deleteTest(selectedTest);
        setDeleteDialogOpen(false);
        setSelectedTest(null);

        toast({
          title: t('testDeletedMsg'),
          description: t('abTestDeletedDesc'),
        });
      } catch (error) {
        toast({
          title: t('deleteErrorTitle'),
          description: t('cannotDeleteTest'),
          variant: "destructive",
        });
      }
    }
  };


  return (
    <Layout fixedSidebar>
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header Premium */}
        <div className="flex-shrink-0 bg-transparent">
          <div className="max-w-[1600px] w-full mx-auto px-4 md:px-8 pt-4 md:pt-4 pb-4 md:pb-5">
            <h1 className="text-[20px] md:text-[22px] font-semibold text-[#1E2330] tracking-tight leading-[1.2]">
              {t('abTestListTitle')}
            </h1>
          </div>
          <div className="hidden lg:block h-px w-full" style={{ backgroundColor: 'rgba(0, 0, 0, 0.08)', marginTop: '1px' }} />
        </div>
        <main className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6 animate-fade-in">
            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-[#1D4FFF]" />
              </div>
            ) : (
              <>
                {tests.length > 0 && (
                  <div className="flex justify-end">
                    <Button onClick={handleCreateTest} className="flex items-center gap-2">
                      <PlusCircle className="h-4 w-4" />
                      {t('createNewTest')}
                    </Button>
                  </div>
                )}

                {tests.length === 0 ? (
                  <EmptyABTestState
                    onCreateNew={handleCreateTest}
                  />
                ) : (
                  <VideoGrid>
                    {tests.map((test) => (
                      <VideoCardUnified
                        key={test.id}
                        id={test.id}
                        variant="list"
                        title={test.name || ''}
                        thumbnail={test.videos?.[0]?.thumbnail || 'https://placehold.co/640x360/gray/white?text=Video'}
                        showToggle={true}
                        toggleValue={test.is_active === true || (test.is_active as any) === 1}
                        onToggleChange={() => handleToggleStatus(test.id)}
                        footerDate={formatDate(test.created_at)}
                        isEditing={editingTestId === test.id}
                        onTitleChange={(newTitle) => handleTitleChange(test.id_teste_ab || test.id, newTitle)}
                        onEditingComplete={() => setEditingTestId(null)}
                        actions={[
                          {
                            label: t('copyCode'),
                            icon: <Copy className="h-4 w-4" />,
                            onClick: () => handleCopyCode(test as any),
                          },
                          {
                            label: t('rename'),
                            icon: <Pencil className="h-4 w-4" />,
                            onClick: () => handleOpenRename(test.id),
                          },
                          {
                            label: t('viewMetrics'),
                            icon: <Eye className="h-4 w-4" />,
                            onClick: () => navigate(`/ab-test/${test.id}/metrics`),
                          },
                          {
                            label: t('delete'),
                            icon: <Trash2 className="h-4 w-4" />,
                            onClick: () => handleDelete(test.id),
                            variant: 'destructive' as const,
                          },
                        ]}
                        onClick={() => navigate(`/ab-test/${test.id}/metrics`)}
                      />
                    ))}
                  </VideoGrid>
                )}
              </>
            )}

        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="!w-[40vh] md:w-[35vh] rounded-xl p-6">
            <AlertDialogHeader>
              <AlertDialogTitle>{t('deleteABTestTitle')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('deleteTestConfirm')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className='h-11 px-5 rounded-[10px] border-border text-muted-foreground hover:bg-muted hover:text-[#64748b] transition-colors'>{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDelete}
                className="bg-red-500 text-white hover:bg-red-600"
              >
                {t('confirmDeleteTest')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        
          </div>
        </main>
      </div>
    </Layout>
  );
};

export default ABTestList;
